import { useState } from 'react'
import { FormDialog } from '@/components/common'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { useLegalEntities } from '@/features/org'
import { toApiProblem } from '@/lib/api/client'
import { useLeaveTypeMutations } from '../api'
import type { LeaveTypeDetailView } from '../types'

const EMPTY = {
  legalEntityId: '', code: '', name: '', paid: true, requiresDocument: false,
  maxConsecutiveDays: '', genderRestriction: '', countsNonWorkingDays: false,
  dayUnit: 'WORKING_DAYS', annualEntitlementDays: '', maxDaysPerYear: '',
  accrualBasis: 'ANNUAL_GRANT', minServiceMonths: '', fullEntitlementAfterMonths: '',
  partialAccrualDaysPerMonth: '', carryForwardMaxDays: '', encashable: false,
  requiresRejoinConfirmation: false, airTicketEligible: false, unbounded: false,
}

type Draft = typeof EMPTY & { id?: string }

/**
 * Defining a leave type.
 *
 * <p>The screen's own empty state said "add types by hand", and there was no way to. The only
 * writes it offered were the statutory pack and the pay-band editor, so any entitlement the
 * pack does not cover — a company's own compassionate leave, an unpaid sabbatical — could not
 * be created at all.
 *
 * <p><b>No figure here has a default.</b> Days of entitlement, service thresholds and carry
 * forward are policy, and a form that pre-fills "21" produces a type nobody chose. Blank means
 * unset, and every unset field says what its absence means rather than reading as zero.
 */
export function LeaveTypeForm({
  editing, open, onOpenChange,
}: {
  editing: LeaveTypeDetailView | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const entities = useLegalEntities()
  const { create, update } = useLeaveTypeMutations()
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState<Draft>(() => toDraft(editing))

  // A different row means a different draft; the key on the caller's side remounts this.
  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((previous) => ({ ...previous, [key]: value }))
  }

  const number = (value: string) => (value === '' ? null : Number(value))

  async function submit() {
    setError(null)
    const body = {
      legalEntityId: draft.legalEntityId || null,
      code: draft.code,
      name: draft.name,
      paid: draft.paid,
      requiresDocument: draft.requiresDocument,
      maxConsecutiveDays: number(draft.maxConsecutiveDays),
      genderRestriction: draft.genderRestriction || null,
      countsNonWorkingDays: draft.countsNonWorkingDays,
      color: null,
      active: true,
      dayUnit: draft.dayUnit,
      annualEntitlementDays: number(draft.annualEntitlementDays),
      maxDaysPerYear: number(draft.maxDaysPerYear),
      accrualBasis: draft.accrualBasis,
      minServiceMonths: number(draft.minServiceMonths),
      fullEntitlementAfterMonths: number(draft.fullEntitlementAfterMonths),
      partialAccrualDaysPerMonth: number(draft.partialAccrualDaysPerMonth),
      carryForwardMaxDays: number(draft.carryForwardMaxDays),
      encashable: draft.encashable,
      requiresRejoinConfirmation: draft.requiresRejoinConfirmation,
      airTicketEligible: draft.airTicketEligible,
      unbounded: draft.unbounded,
    }
    try {
      await (draft.id
        ? update.mutateAsync({ id: draft.id, ...body })
        : create.mutateAsync(body))
      onOpenChange(false)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That leave type could not be saved.')
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={draft.id ? `Edit ${editing?.name ?? 'leave type'}` : 'New leave type'}
      description="Nothing here is filled in for you: days of entitlement and service thresholds are policy, and a default would be a policy nobody chose."
      size="lg"
      submitLabel="Save leave type"
      loading={create.isPending || update.isPending}
      error={error}
      onSubmit={() => { void submit() }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field id="lt-code" label="Code">
          <Input id="lt-code" value={draft.code}
            onChange={(event) => set('code', event.target.value.toUpperCase())} />
        </Field>
        <Field id="lt-name" label="Name">
          <Input id="lt-name" value={draft.name}
            onChange={(event) => set('name', event.target.value)} />
        </Field>
        <Field id="lt-entity" label="Legal entity"
          hint="Leave blank for a type every entity uses."
        >
          <Select id="lt-entity" className="w-full" value={draft.legalEntityId}
            onChange={(event) => set('legalEntityId', event.target.value)}
          >
            <option value="">Every entity</option>
            {entities.data?.map((entity) => (
              <option key={entity.id} value={entity.id}>{entity.name}</option>
            ))}
          </Select>
        </Field>
        <Field id="lt-day-unit" label="Counted in"
          hint="Calendar days include the weekend; working days do not. It changes what a Friday-to-Monday absence costs."
        >
          <Select id="lt-day-unit" className="w-full" value={draft.dayUnit}
            onChange={(event) => set('dayUnit', event.target.value)}
          >
            <option value="WORKING_DAYS">Working days</option>
            <option value="CALENDAR_DAYS">Calendar days</option>
          </Select>
        </Field>
        <Field id="lt-accrual" label="Accrual">
          <Select id="lt-accrual" className="w-full" value={draft.accrualBasis}
            onChange={(event) => set('accrualBasis', event.target.value)}
          >
            <option value="ANNUAL_GRANT">Granted once a year</option>
            <option value="MONTHLY_ACCRUAL">Accrued monthly</option>
            <option value="PER_OCCURRENCE">Per occurrence</option>
          </Select>
        </Field>
        <Field id="lt-annual" label="Annual entitlement (days)"
          hint="Blank means the entitlement is not a fixed number of days."
        >
          <Input id="lt-annual" type="number" min={0} step="0.5"
            value={draft.annualEntitlementDays}
            onChange={(event) => set('annualEntitlementDays', event.target.value)} />
        </Field>
        <Field id="lt-max-year" label="Maximum a year (days)"
          hint="A cap regardless of accrual. Blank means no cap."
        >
          <Input id="lt-max-year" type="number" min={0} step="0.5" value={draft.maxDaysPerYear}
            onChange={(event) => set('maxDaysPerYear', event.target.value)} />
        </Field>
        <Field id="lt-consecutive" label="Maximum in one go (days)"
          hint="Blank means no limit on a single absence."
        >
          <Input id="lt-consecutive" type="number" min={0} value={draft.maxConsecutiveDays}
            onChange={(event) => set('maxConsecutiveDays', event.target.value)} />
        </Field>
        <Field id="lt-min-service" label="Service before any entitlement (months)"
          hint="Blank means it applies from the first day."
        >
          <Input id="lt-min-service" type="number" min={0} value={draft.minServiceMonths}
            onChange={(event) => set('minServiceMonths', event.target.value)} />
        </Field>
        <Field id="lt-carry" label="Carry forward (days)"
          hint="Blank means nothing carries into the next year."
        >
          <Input id="lt-carry" type="number" min={0} step="0.5" value={draft.carryForwardMaxDays}
            onChange={(event) => set('carryForwardMaxDays', event.target.value)} />
        </Field>
        <Field id="lt-gender" label="Restricted to"
          hint="For an entitlement the law gives to one group — maternity, paternity."
        >
          <Select id="lt-gender" className="w-full" value={draft.genderRestriction}
            onChange={(event) => set('genderRestriction', event.target.value)}
          >
            <option value="">Everybody</option>
            <option value="MALE">Male</option>
            <option value="FEMALE">Female</option>
          </Select>
        </Field>

        <fieldset className="space-y-2 sm:col-span-2">
          <legend className="text-sm font-medium text-[var(--color-heading)]">
            How it behaves
          </legend>
          <Toggle checked={draft.paid} onChange={(value) => set('paid', value)}
            label="Paid" hint="Unpaid leave still counts against attendance; it just is not paid." />
          <Toggle checked={draft.requiresDocument}
            onChange={(value) => set('requiresDocument', value)}
            label="Needs supporting evidence"
            hint="A medical certificate, for instance. The request cannot be approved without one." />
          <Toggle checked={draft.countsNonWorkingDays}
            onChange={(value) => set('countsNonWorkingDays', value)}
            label="Counts weekends and holidays inside the absence" />
          <Toggle checked={draft.encashable} onChange={(value) => set('encashable', value)}
            label="Encashable on leaving" />
          <Toggle checked={draft.requiresRejoinConfirmation}
            onChange={(value) => set('requiresRejoinConfirmation', value)}
            label="Return has to be confirmed"
            hint="Somebody records the actual return date, so a late return is caught rather than assumed." />
          <Toggle checked={draft.airTicketEligible}
            onChange={(value) => set('airTicketEligible', value)}
            label="Uses the air-ticket entitlement" />
          <Toggle checked={draft.unbounded} onChange={(value) => set('unbounded', value)}
            label="Keeps no balance"
            hint="No balance is kept; every request is approved on its merits." />
        </fieldset>
      </div>
    </FormDialog>
  )
}

function toDraft(type: LeaveTypeDetailView | null): Draft {
  if (!type) {
    return { ...EMPTY }
  }
  const text = (value: number | null | undefined) => (value == null ? '' : String(value))
  return {
    id: type.id,
    legalEntityId: type.legalEntityId ?? '',
    code: type.code,
    name: type.name,
    paid: type.paid,
    requiresDocument: type.requiresDocument,
    maxConsecutiveDays: text(type.maxConsecutiveDays),
    genderRestriction: type.genderRestriction ?? '',
    countsNonWorkingDays: type.countsNonWorkingDays,
    dayUnit: type.dayUnit,
    annualEntitlementDays: text(type.annualEntitlementDays),
    maxDaysPerYear: text(type.maxDaysPerYear),
    accrualBasis: type.accrualBasis,
    unbounded: type.unbounded,
    minServiceMonths: text(type.minServiceMonths),
    fullEntitlementAfterMonths: text(type.fullEntitlementAfterMonths),
    partialAccrualDaysPerMonth: text(type.partialAccrualDaysPerMonth),
    carryForwardMaxDays: text(type.carryForwardMaxDays),
    encashable: type.encashable,
    requiresRejoinConfirmation: type.requiresRejoinConfirmation,
    airTicketEligible: type.airTicketEligible,
  }
}

function Field({
  id, label, hint, children,
}: {
  id: string
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint && <p className="text-xs text-[var(--color-muted)]">{hint}</p>}
    </div>
  )
}

function Toggle({
  checked, onChange, label, hint,
}: {
  checked: boolean
  onChange: (value: boolean) => void
  label: string
  hint?: string
}) {
  return (
    <label className="flex items-start gap-2 text-sm">
      <input
        type="checkbox"
        className="mt-1"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>
        {label}
        {hint && <span className="block text-xs text-[var(--color-muted)]">{hint}</span>}
      </span>
    </label>
  )
}
